import { Badge, BadgeVariant } from '@/components/ui/badge'
import type { TimelineEvent } from '@/types/timeline'
import { AssignmentEventTypeBadge, type AssignmentEventType, type BaseEvent } from '@/types/assignment'
import type { UUID, Instant } from '@/types/common'
import { Clock, User, FileText, Loader2 } from 'lucide-react'
import { formatDate } from '@/lib/date-utils'
import { useI18n } from '@/i18n'
import { useAssignmentEventTypeLabel } from '@/features/identity/labels'
import {EmptyState} from '@/components/shared/EmptyState'

/**
 * Display-shape consumed by {@link AccessTimeline}. Both AP timeline events
 * (which carry resource + system) and FG timeline events (which don't) map
 * onto this single shape — `subtitle` is optional and only rendered when
 * non-empty (issue 549).
 */
export interface TimelineEntry extends BaseEvent {
    id: UUID
    eventType: AssignmentEventType
    title: string
    subtitle?: string | null
    createdAt: Instant
}

interface AccessTimelineProps<E extends TimelineEvent | TimelineEntry> {
    events: E[]
    isLoading: boolean
    onEventClick?: (event: E) => void
}

function toEntry(e: TimelineEvent | TimelineEntry): TimelineEntry {
    if ('title' in e) {
        return e
    }
    const sub = [e.resourceName, e.systemName].filter(Boolean).join(' \u2022 ')
    return {
        ...e,
        title: e.accessProfileName,
        subtitle: sub.length > 0 ? sub : null,
    }
}

function dotColorFor(eventType: AssignmentEventType): string {
    const variant = AssignmentEventTypeBadge.of(eventType)?.variant
    /* eslint-disable i18next/no-literal-string -- CSS utility classes */
    if (variant === BadgeVariant.SUCCESS) return 'bg-success'
    if (variant === BadgeVariant.DESTRUCTIVE) return 'bg-danger'
    if (variant === BadgeVariant.WARNING) return 'bg-warning'
    return 'bg-primary'
    /* eslint-enable i18next/no-literal-string */
}

export function AccessTimeline<E extends TimelineEvent | TimelineEntry>({ events, isLoading, onEventClick }: AccessTimelineProps<E>) {
    const i18n = useI18n()
    const eventTypeLabel = useAssignmentEventTypeLabel()

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                <span className="ml-2 text-muted-foreground" data-testid="timeline-loading">{i18n.accessTimeline.loadingTimeline}</span>
            </div>
        )
    }

    if (events.length === 0) {
        return (
            <div data-testid="timeline-empty">
                <EmptyState
                    icon={Clock}
                    title={String(i18n.accessTimeline.noAccessHistory)}
                />
            </div>
        )
    }

    return (
        <div className="relative" data-testid="access-timeline">
            {/* Vertical line */}
            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-border" />

            <div className="space-y-4">
                {events.map((original) => {
                    const event = toEntry(original)
                    const badge = AssignmentEventTypeBadge.of(event.eventType)
                    return (
                        <div key={event.id} className="relative pl-10" data-testid={`timeline-event-${event.id}`}>
                            {/* Timeline dot */}
                            <div
                                className={`absolute left-2.5 top-1 h-3 w-3 rounded-full border-2 border-background ${dotColorFor(event.eventType)}`}
                            />

                            <div
                                className={`bg-card border rounded-lg p-4 ${onEventClick ? 'cursor-pointer hover:bg-muted/50 transition-colors' : ''}`}
                                onClick={() => onEventClick?.(original)}
                            >
                                <div className="flex items-start justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                        <Badge variant={badge?.variant ?? BadgeVariant.OUTLINE} data-testid={`timeline-event-type-${event.id}`}>{eventTypeLabel(event.eventType)}</Badge>
                                        <span className="font-medium" data-testid={`timeline-profile-${event.id}`}>{event.title}</span>
                                    </div>
                                    <span className="text-sm text-muted-foreground">
                                        {formatDate(event.createdAt)}
                                    </span>
                                </div>

                                {event.subtitle && (
                                    <div className="text-sm text-muted-foreground mb-2">
                                        {event.subtitle}
                                    </div>
                                )}

                                {event.performedBy && (
                                    <div className="flex items-center gap-1 text-sm text-muted-foreground mb-2" data-testid={`timeline-performer-${event.id}`}>
                                        <User className="h-3 w-3" />
                                        <span>{i18n.accessTimeline.by({ name: event.performedBy.displayName })}</span>
                                    </div>
                                )}

                                {event.sourceName && (
                                    <div className="flex items-center gap-1 text-sm text-muted-foreground mb-2" data-testid={`timeline-source-${event.id}`}>
                                        <FileText className="h-3 w-3" />
                                        <span>{i18n.accessTimeline.sourceLabel({ name: event.sourceName })}</span>
                                    </div>
                                )}

                                {event.comment && (
                                    <div className="text-sm italic text-muted-foreground border-l-2 border-muted pl-2 mt-2" data-testid={`timeline-comment-${event.id}`}>
                                        {event.comment}
                                    </div>
                                )}

                                {(event.oldExpiresAt || event.newExpiresAt) && (
                                    <div className="text-xs text-muted-foreground mt-2" data-testid={`timeline-expiry-${event.id}`}>
                                        {event.oldExpiresAt && (
                                            <span>{i18n.common.from} {formatDate(event.oldExpiresAt)}</span>
                                        )}
                                        { }
                                        {event.oldExpiresAt && event.newExpiresAt && ' \u2192 '}
                                        {event.newExpiresAt && (
                                            <span>{i18n.common.to} {formatDate(event.newExpiresAt)}</span>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}
